import api from 'core/util/api';
import {
  checkChannelForConjunction,
  findNotificationsByPolicy as utilFindNotificationsByPolicy,
  findNotificationsByPolicyDescriptor as utilFindNotificationsByPolicyDescriptor,
  findNotificationsByAiInvestigationRule as utilFindNotificationsByAiInvestigationRule
} from '@kentik/ui-shared/notifications/notificationUtilities';

import { NOTIFICATION_TEST_AND_PREVIEW_TYPES } from '@kentik/ui-shared/notifications/constants';
import SupportedChannelsCollection from './SupportedChannelsCollection';
import NotificationsCollection from './NotificationsCollection';
import NotificationModel from './NotificationModel';

export class NotificationsStore {
  collection = new NotificationsCollection();

  supportedChannels = new SupportedChannelsCollection();

  userChannel = new NotificationModel();

  // these channel types support OAUTH2.0 tokens, if users can configure integration for them
  oAuthSupportedChannelTypes = ['custom-webhook'];

  constructor(options = {}) {
    Object.assign(this, options);
  }

  initialize() {
    const { $app } = this.store;
    if (!$app.isSubtenant) {
      this.fetchUserChannel();
    }
  }

  get usedByOptions() {
    return [
      { value: 'policies', label: 'Policies' },
      { value: 'mitigationMethod', label: 'Mitigation Methods' },
      { value: 'insights', label: 'Insights' },
      { value: 'insightFamilies', label: 'Insight Families' },
      { value: 'subpolicies', label: 'Tenant policies' },
      { value: 'synthTest', label: 'Synthetics Tests' },
      { value: 'aiInvestigation', label: 'Auto Investigation' },
      { value: 'unknown', label: 'Other' }
    ];
  }

  get supportedNotificationTypesForPreviewOptions() {
    return [
      { value: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_INSIGHT_ALARM_MODEL, label: 'Insight' },
      { value: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_ALARM_MODEL, label: 'Alert' },
      { value: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_NMS_ALARM_MODEL, label: 'NMS Alert' },
      { value: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_MITIGATION_ALARM_MODEL, label: 'Mitigation' },
      { value: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_SYNTH_ALARM_MODEL, label: 'Synthetics' }
    ];
  }

  channelTypeSupportOAuth(channelType) {
    return this.oAuthSupportedChannelTypes.includes(channelType);
  }

  addNotificationsToThresholds(thresholds = [], policy = {}) {
    const ruleId = policy.activationSettings?.ruleId;

    return thresholds.map((threshold) => {
      const channelIds = this.findNotificationsByPolicyDescriptor(threshold, ruleId).map(({ id }) => id);

      return {
        ...threshold,
        notificationChannels: channelIds
      };
    });
  }

  addNotificationsToNmsPolicyLevels(levels = [], policy = {}) {
    const ruleId = policy.rule_id;

    return levels.map((level) => {
      const channelIds = this.findNotificationsByPolicyDescriptor(level, ruleId).map(({ id }) => id);

      return {
        ...level,
        notificationChannels: channelIds
      };
    });
  }

  // find notifications that use this specific threshold or level (policy concepts)
  findNotificationsByPolicyDescriptor = (descriptor, ruleId) =>
    utilFindNotificationsByPolicyDescriptor({ descriptor, ruleId, channels: this.collection.get() });

  // find all notifications that use any threshold on this policy
  findNotificationsByPolicy = (policy) => utilFindNotificationsByPolicy({ policy, channels: this.collection.get() });

  // find notifications that use this mitigation method
  findNotificationsByMitigationMethod = (mitigationMethodID) =>
    this.collection.get().filter((channel) =>
      checkChannelForConjunction(channel, {
        selectors: [{ lhs: 'mitigationMethod', op: '=', rhs: `${mitigationMethodID}` }]
      })
    );

  // find notifications that have AI investigation selectors for this rule
  findNotificationsByAiInvestigationRule = (ruleId) =>
    utilFindNotificationsByAiInvestigationRule({ ruleId, channels: this.collection.get() });

  fetchUserChannel = () =>
    api.get('/api/ui/notifications/user').then((response) => {
      if (response.channel) {
        this.userChannel.set(this.userChannel.deserialize(response.channel));
      }
    });

  subscribeInsight = ({ subscribe = true, subscribeFamily = true, model }) =>
    api
      .post('/api/ui/notifications/subscribeInsight', {
        data: {
          subscribe,
          subscribeFamily,
          insightName: model.get('insightName'),
          insightFamily: model.family
        }
      })
      .then(() => this.fetchUserChannel());

  previewChannel = (previewPayload) =>
    api
      .post('/api/ui/notifications/preview', {
        data: {
          ...previewPayload,
          // do not pass a test model if we have an actual event model
          test_model: previewPayload.notification_event
            ? undefined
            : previewPayload.test_model || { model_type: NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_ALARM_MODEL }
        }
      })
      .then((response) => {
        if (response.error) {
          return {
            content: '',
            error: response.error
          };
        }
        try {
          const parsed = JSON.parse(response.payload);

          return {
            content: JSON.stringify(parsed, null, '  '),
            error: null
          };
        } catch (e) {
          return {
            content: (response && response.payload) || '',
            error: null
          };
        }
      });

  testChannelById = (channelId, notificationType) => {
    const channelModel = this.collection.modelById[channelId];
    const testPayload = {
      channel: channelModel.serialize(channelModel.get()),
      test_model: { model_type: notificationType || NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_ALARM_MODEL }
    };

    if (channelModel) {
      return this.testChannel(testPayload);
    }

    return Promise.reject(new Error('Invalid channel selection for testing.'));
  };

  testChannel = (testPayload) =>
    api.post('/api/ui/notifications/test', {
      data: {
        ...testPayload,
        // do not pass a test model if we have an actual event model
        test_model: testPayload.notification_event
          ? undefined
          : testPayload.test_model || NOTIFICATION_TEST_AND_PREVIEW_TYPES.TEST_ALARM_MODEL
      }
    });

  renderTemplate = (template, data) =>
    api
      .post('/api/ui/notifications/templates/render', { data: { template, data } })
      .then((response) => ({ ok: !response.error, json: JSON.stringify(response) }))
      .catch((error) => ({ ok: false, error: error?.message || 'Render failed' }));

  getTemplateSchema = () =>
    api
      .get('/api/ui/notifications/templates/schema')
      .then((response) => ({ ok: !response.error, data: response }))
      .catch((error) => ({ ok: false, error: error?.message || 'Schema fetch failed' }));

  getNotificationViewModel = (testPayload) =>
    api.post('/api/ui/notifications/view-model', {
      data: testPayload
    });

  listPresetTemplates = () =>
    api
      .get('/api/ui/notifications/templates/presets')
      .then((response) => ({ ok: true, data: response }))
      .catch((error) => ({ ok: false, error: error?.message || 'Failed to load presets' }));

  // right now unused by user-facing code, AIA is the only consumer
  validateTemplate = (template, format) =>
    api
      .post('/api/ui/notifications/templates/validate', { data: { template, format } })
      .then((response) => ({ ok: true, data: response }))
      .catch((error) => ({ ok: false, error: error?.message || 'Validation failed' }));
}

export default new NotificationsStore();
